import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ClipboardDocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { objectStorageService } from '../../services/objectStorageService';

interface Props {
  projectId: string;
  bucketId: string;
  objectKey: string;
  onClose: () => void;
}

export default function PresignModal({ projectId, bucketId, objectKey, onClose }: Props) {
  const [operation, setOperation] = useState<'get' | 'put'>('get');
  const [expiryMinutes, setExpiryMinutes] = useState(60);
  const [generatedUrl, setGeneratedUrl] = useState('');

  const presignMutation = useMutation({
    mutationFn: () =>
      objectStorageService.presignObject(projectId, bucketId, objectKey, operation, expiryMinutes),
    onSuccess: (data) => setGeneratedUrl(data.url),
    onError: () => toast.error('Failed to generate URL'),
  });

  const copyUrl = () => {
    navigator.clipboard.writeText(generatedUrl);
    toast.success('URL copied to clipboard');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 w-full max-w-lg mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Generate Pre-authenticated URL</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Object Key</label>
            <p className="text-sm text-white font-mono bg-gray-900 px-3 py-2 rounded-lg truncate">
              {objectKey}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Operation</label>
              <select
                value={operation}
                onChange={(e) => setOperation(e.target.value as 'get' | 'put')}
                className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-primary-500"
              >
                <option value="get">GET (download)</option>
                <option value="put">PUT (upload)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Expiry (minutes)</label>
              <input
                type="number"
                min={1}
                max={10080}
                value={expiryMinutes}
                onChange={(e) => setExpiryMinutes(Number(e.target.value))}
                className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>

          <button
            onClick={() => presignMutation.mutate()}
            disabled={presignMutation.isPending}
            className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white font-medium py-2 rounded-lg transition-colors"
          >
            {presignMutation.isPending ? 'Generating…' : 'Generate URL'}
          </button>

          {generatedUrl && (
            <div>
              <label className="block text-sm text-gray-400 mb-1">Pre-authenticated URL</label>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={generatedUrl}
                  className="flex-1 bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-xs text-gray-300 font-mono focus:outline-none"
                />
                <button
                  onClick={copyUrl}
                  className="flex-shrink-0 p-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors"
                  title="Copy URL"
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
